import { useCallback, useState } from 'react'
import {
  PUT_UPDATE_PAY_SCHEDULE_KEY,
  putUpdatePaymentConfig,
  putUpdatePaySchedule,
} from '../payrollActions'
import { Button, Dialog, Text, Link, Alert, Switch } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError } from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'
import { Gusto_PaySchedule } from '../generated_gusto_types'
import { TextColors, TypographyStyles } from '@/styles'

export const AutoEnrollModal = ({
  modalOpen,
  onClose,
  paySchedule,
}: {
  modalOpen: boolean
  onClose: () => void
  paySchedule: Gusto_PaySchedule
}) => {
  const dispatch = useAppDispatch()
  const [modalAutomated, setModalAutomated] = useState(paySchedule.auto_payroll)
  const error = useReselector(getFetchError, PUT_UPDATE_PAY_SCHEDULE_KEY)

  const onSave = useCallback(async () => {
    if (modalAutomated === paySchedule.auto_payroll) {
      onClose()
      return
    }

    await dispatch(
      putUpdatePaymentConfig({
        payment_speed: '4-day',
      })
    )

    const res = await putUpdatePaySchedule(paySchedule.uuid, {
      version: paySchedule.version,
      auto_payroll: modalAutomated,
    })(dispatch)

    if (res) {
      onClose()
    }
  }, [
    paySchedule.uuid,
    paySchedule.auto_payroll,
    paySchedule.version,
    modalAutomated,
    dispatch,
    onClose,
  ])

  const cancelModal = useCallback(() => {
    onClose()
    setModalAutomated(paySchedule.auto_payroll)
  }, [onClose, paySchedule.auto_payroll])

  return (
    <Dialog open={modalOpen} onOpenChange={cancelModal}>
      <Dialog.Content className="max-w-sm w-full">
        <Dialog.Title>Update Automatic Payroll</Dialog.Title>
        <div className="flex flex-col gap-4 mt-4">
          <Text variant={TypographyStyles.BODY_MD}>
            Automatic Payroll allows us to run your payroll automatically, right
            on schedule! Each pay period, we&apos;ll automatically run payroll
            for you, one day before your payroll deadline.
          </Text>

          <Alert variant="warning">
            This will turn your payment speed to 4 day.
          </Alert>

          {error && (
            <Alert variant="error">
              There was an error setting up Automatic Payroll.{' '}
              <Link
                href="https://support.gusto.com/article/999755421000000/Use-and-manage-Payroll-on-AutoPilot"
                newPage
              >
                Learn more here.
              </Link>{' '}
              If you have further questions, please{' '}
              <Link href="/conversations">contact us</Link>.
            </Alert>
          )}

          <div className="flex items-center justify-between pt-2">
            <Text variant={TypographyStyles.BODY_MD}>Automatic Payroll</Text>
            <div className="flex items-center gap-4">
              <Text
                variant={TypographyStyles.BODY_MD}
                color={modalAutomated ? TextColors.GREEN : TextColors.DANGER}
              >
                {modalAutomated ? 'ON' : 'OFF'}
              </Text>
              <Switch
                id="modal-automated-switch"
                checked={modalAutomated}
                onCheckedChange={(checked) => setModalAutomated(checked)}
              />
            </div>
          </div>
        </div>
        <Dialog.Footer className="flex justify-end gap-2 mt-4">
          <Button onClick={cancelModal} variant="secondary">
            Cancel
          </Button>
          <Button onClick={onSave}>Save</Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
